import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '../../elements/icon';
import { Accordion } from '../../elements/Accordion';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import PractitionerBadge from '../../Customer/components/PractitionerBadge';
import ReviewsMobile from '../../Customer/components/ReviewsMobile';
import BoomTestimonials from '../../Customer/components/BoomTestimonials';
import MakeChoice from '../../Home/MakeChoice';
import IngredientsSlider from '../../Home/Slider/IngredientsSlider';
import DiscountBanner from '../../Home/DiscountBanner';
import Header from '../../../Layout/Header';
import { LP_LANGUAGE_OPTIONS, formatNumber } from '../../../utils/language';
import useReviewStats from '../../../hooks/useReviewStats';
import { Review1, Review2, Review3, Review4, ReviewRating } from '../../../Constants';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImage from '../../../assets/hero-lp-pills.webp';
import RiisePouch from '../../../assets/yellow-paper-pouch-granules.webp';
import JanPhoto from '../../../assets/jan-testimonial.jpg';
import ResultsCustomerPhoto from '../../../assets/testimonials/vanessa-kitchen.webp';

const K = {
  HeroH1Part1:         'LP_Pills_HeroH1Part1',
  HeroH1Part2:         'LP_Pills_HeroH1Part2',
  HeroSubline:         'LP_Pills_HeroSubline',
  HeroCta:             'LP_Pills_HeroCta',
  HeroSocialProof:     'LP_Pills_HeroSocialProof',
  StickyLine1:         'LP_Pills_StickyLine1',
  StickyLine2:         'LP_Pills_StickyLine2',
  Stat1Val:            'LP_Pills_Stat1Val',
  Stat1Label:          'LP_Pills_Stat1Label',
  Stat2Val:            'LP_Pills_Stat2Val',
  Stat2Label:          'LP_Pills_Stat2Label',
  Stat3Val:            'LP_Pills_Stat3Val',
  Stat3Label:          'LP_Pills_Stat3Label',
  Stat4Val:            'LP_Pills_Stat4Val',
  Stat4Label:          'LP_Pills_Stat4Label',
  ProbSectionLabel:    'LP_Pills_ProbSectionLabel',
  ProbH2Line1:         'LP_Pills_ProbH2Line1',
  ProbH2Line2:         'LP_Pills_ProbH2Line2',
  ProbSubline:         'LP_Pills_ProbSubline',
  OldWayTitle:         'LP_Pills_OldWayTitle',
  OldWay1:             'LP_Pills_OldWay1',
  OldWay2:             'LP_Pills_OldWay2',
  OldWay3:             'LP_Pills_OldWay3',
  OldWay4:             'LP_Pills_OldWay4',
  OldWay5:             'LP_Pills_OldWay5',
  NewWayTitle:         'LP_Pills_NewWayTitle',
  NewWay1:             'LP_Pills_NewWay1',
  NewWay2:             'LP_Pills_NewWay2',
  NewWay3:             'LP_Pills_NewWay3',
  NewWay4:             'LP_Pills_NewWay4',
  NewWay5:             'LP_Pills_NewWay5',
  ResultsSectionLabel: 'LP_Pills_ResultsSectionLabel',
  ResultsH1:           'LP_Pills_ResultsH1',
  ResultsH2:           'LP_Pills_ResultsH2',
  ResultsStat1Val:     'LP_Pills_ResultsStat1Val',
  ResultsStat1Desc:    'LP_Pills_ResultsStat1Desc',
  ResultsStat2Val:     'LP_Pills_ResultsStat2Val',
  ResultsStat2Desc:    'LP_Pills_ResultsStat2Desc',
  ResultsSource:       'LP_Pills_ResultsSource',
  HowWorksHeading:     'LP_Pills_HowWorksHeading',
  Step1Title:          'LP_Pills_Step1Title',
  Step1Desc:           'LP_Pills_Step1Desc',
  Step2Title:          'LP_Pills_Step2Title',
  Step2Desc:           'LP_Pills_Step2Desc',
  Step3Title:          'LP_Pills_Step3Title',
  Step3Desc:           'LP_Pills_Step3Desc',
  ExpertHeading:       'LP_Pills_ExpertHeading',
  IngrHeading:         'LP_Pills_IngrHeading',
  IngrSubline:         'LP_Pills_IngrSubline',
  FeatQuote:           'LP_Pills_FeatQuote',
  FeatAuthor:          'LP_Pills_FeatAuthor',
  ReviewsHeading:      'LP_Pills_ReviewsHeading',
  Faq1Q:               'LP_Pills_Faq1Q',
  Faq1A:               'LP_Pills_Faq1A',
  Faq2Q:               'LP_Pills_Faq2Q',
  Faq2A:               'LP_Pills_Faq2A',
  Faq3Q:               'LP_Pills_Faq3Q',
  Faq3A:               'LP_Pills_Faq3A',
  Faq4Q:               'LP_Pills_Faq4Q',
  Faq4A:               'LP_Pills_Faq4A',
  Faq5Q:               'LP_Pills_Faq5Q',
  Faq5A:               'LP_Pills_Faq5A',
  FaqHeading:          'LP_Pills_FaqHeading',
  FinalHeading:        'LP_Pills_FinalHeading',
  FinalSubline:        'LP_Pills_FinalSubline',
  FinalCta:            'LP_Pills_FinalCta',
  AdDisclosure:        'LP_Pills_AdDisclosure',
  PrivacyPolicy:       'LP_Pills_PrivacyPolicy',
  PageTitle:           'LP_Pills_PageTitle',
  BadgeLabelLine1:     'LP_BadgeLabelLine1',
  BadgeLabelLine2:     'LP_BadgeLabelLine2',
  BadgeText:           'LP_BadgeText',
  CtaSub:              'LP_CtaSub',
};

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true }) => {
  const { metaEvent } = useMeta();
  return (
  <CustomLink
    to="/quiz"
    onClick={() => metaEvent('Lead')}
    className="lp-btn-cta"
    style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, background: '#F6E147', color: '#103642',
      fontWeight: 700, fontSize: 16, borderRadius: 999,
      padding: '18px 36px', textDecoration: 'none',
      width: full ? '100%' : 'auto' }}
  >
    {children}
    <span aria-hidden="true">→</span>
  </CustomLink>
  );
};

const Label = ({ children, pill = false }) => {
  const base = {
    ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#116D6E',
    display: 'block', textAlign: 'center', margin: 0,
  };
  if (pill) return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...base, background: 'rgba(17,109,110,0.1)', borderRadius: 999, padding: '6px 16px' }}>
        {children}
      </span>
    </div>
  );
  return <p style={base}>{children}</p>;
};

const StepNumber = ({ n }) => (
  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SANS,
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

const StarRow = ({ count = 5 }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

const TrendUpIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="15 7 21 7 21 13" />
  </svg>
);

const ThumbsUpIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z" />
    <path d="M7 11l4-7a2 2 0 0 1 2 2v4h5.5a2 2 0 0 1 1.94 2.5l-1.5 6A2 2 0 0 1 17 20H9a2 2 0 0 1-2-2" />
  </svg>
);

const Section = ({ bg = '#fff', py = 64, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const StickyBar = ({ t, K, isDesktop, visible }) => {
  const { metaEvent } = useMeta();

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
      transform: visible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.8)',
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          {t(K.StickyLine1)}<br />
          <span style={{ color: '#F6E147', fontWeight: 600 }}>{t(K.StickyLine2)}</span>
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

const PillsOrSachet = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reviewStats = useReviewStats();
  const fmtRating = (value) => formatNumber(value, i18n.language, { maximumFractionDigits: 2 });
  const fmtCount = (value) => formatNumber(value, i18n.language);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1200
  );

  // Landing pages render outside AppLayout, so the global ?lang= handler in
  // Layout.jsx never runs and i18n stays on its init value ('en'). This is a
  // German ad LP — default to German unless the URL explicitly requests English.
  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const heroRef = useRef(null);
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPastHero(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const maxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>{t(K.PageTitle)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <DiscountBanner />
      <Header languageOptions={LP_LANGUAGE_OPTIONS} />
      <StickyBar t={t} K={K} isDesktop={isDesktop} visible={pastHero} />

      {/* ── Hero ── */}
      {isDesktop ? (
        <section ref={heroRef} style={{ background: '#F9F9F9', overflow: 'hidden' }}>
          <div style={{
            maxWidth: 1400, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '52% 48%',
            minHeight: 600,
          }}>
            {/* Left: content */}
            <div style={{ padding: '56px 64px 56px 80px', display: 'flex', alignItems: 'center' }}>
              <Stack gap={24} style={{ width: '100%', maxWidth: 560 }}>
                <PractitionerBadge />
                <h1 style={{ ...SERIF, fontSize: 58, lineHeight: 1.06, fontWeight: 400,
                  color: '#103642', margin: 0 }}>
                  {t(K.HeroH1Part1)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
                </h1>
                <p style={{ ...SANS, fontSize: 18, color: 'rgba(16,54,66,0.7)',
                  lineHeight: 1.7, margin: 0 }}>
                  {t(K.HeroSubline)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#F6E147', fontSize: 17, letterSpacing: '-1px' }}>★★★★★</span>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                    {t(K.HeroSocialProof, {
                      rating: fmtRating(reviewStats?.average || 4.88),
                      count: fmtCount(reviewStats?.count || 5000),
                    })}
                  </p>
                </div>
                <Stack gap={12} style={{ maxWidth: 380 }}>
                  <CtaButton full={false}>{t(K.HeroCta)}</CtaButton>
                  <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                    margin: 0, textAlign: 'center' }}>
                    {t(K.CtaSub)}
                  </p>
                </Stack>
              </Stack>
            </div>
            {/* Right: image */}
            <div style={{ position: 'relative', overflow: 'hidden', display: 'flex',
              alignItems: 'center', justifyContent: 'center' }}>
              <img src={HeroImage} alt="riise personalised sachet"
                style={{ width: '100%', maxWidth: 520, height: 'auto', display: 'block' }} loading="eager" />
            </div>
          </div>
        </section>
      ) : (
        <section ref={heroRef} style={{ background: '#F9F9F9' }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '28px 24px 40px' }}>
            <Stack gap={20}>
              <PractitionerBadge />
              <h1 style={{ ...SERIF, fontSize: 40, lineHeight: 1.08, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.HeroH1Part1)}{' '}
                <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
              </h1>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                lineHeight: 1.7, margin: 0 }}>
                {t(K.HeroSubline)}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#F6E147', fontSize: 15, letterSpacing: '-1px' }}>★★★★★</span>
                <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                  {t(K.HeroSocialProof, {
                    rating: fmtRating(reviewStats?.average || 4.88),
                    count: fmtCount(reviewStats?.count || 5000),
                  })}
                </p>
              </div>
              <Stack gap={12}>
                <CtaButton>{t(K.HeroCta)}</CtaButton>
                <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                  textAlign: 'center', margin: 0 }}>
                  {t(K.CtaSub)}
                </p>
              </Stack>
            </Stack>
          </div>
          <img src={HeroImage} alt="riise personalised sachet"
            style={{ width: '100%', height: 'auto', display: 'block' }} loading="eager" />
        </section>
      )}

      {/* ── Review ticker ── */}
      <div style={{ background: '#103642', padding: '8px 0', overflow: 'hidden' }}>
        <style>{`@keyframes riiseMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div
          style={{ display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
            animation: 'riiseMarquee 45s linear infinite' }}
        >
          {[0, 1].map((dup) => (
            <div key={dup} style={{ display: 'flex', alignItems: 'center',
              color: '#F9F9F9', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14 }}>
              {[
                t(Review1),
                t(Review2),
                t(Review3),
                t(Review4),
                <span key="rating" style={{ color: '#F6E147', fontWeight: 600 }}>
                  {t(ReviewRating, { rating: fmtRating(reviewStats?.average || 4.88) })}
                </span>,
              ].map((item, i) => (
                <React.Fragment key={i}>
                  <span style={{ padding: '0 16px' }}>{item}</span>
                  <span style={{ color: '#F6E147' }}>·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Stats strip ── */}
      <section style={{ background: '#103642', padding: `56px ${px}px` }}>
        <div style={{
          maxWidth: maxW, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr',
          gap: isDesktop ? '0 40px' : '48px 0',
        }}>
          {[
            { val: K.Stat1Val, label: K.Stat1Label },
            { val: K.Stat2Val, label: K.Stat2Label },
            { val: K.Stat3Val, label: K.Stat3Label },
            {
              val: K.Stat4Val, label: K.Stat4Label,
              params: {
                rating: fmtRating(reviewStats?.average || 4.9),
                count: fmtCount(reviewStats?.count || 146),
              },
            },
          ].map(({ val, label, params }) => (
            <Stack key={val} gap={8} align="center" className="lp-stat" style={{ textAlign: 'center' }}>
              <p style={{ ...SERIF, fontSize: isDesktop ? 60 : 52, lineHeight: 1, fontWeight: 400,
                color: '#F6E147', margin: 0 }}>
                {t(val, params)}
              </p>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.4, maxWidth: 140, margin: 0 }}>
                {t(label, params)}
              </p>
            </Stack>
          ))}
        </div>
      </section>

      {/* ── Problem section ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label>{t(K.ProbSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ProbH2Line1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ProbH2Line2)}</em>
          </h2>

          <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)',
            lineHeight: 1.7, margin: 0, textAlign: 'center',
            maxWidth: isDesktop ? 640 : undefined, alignSelf: isDesktop ? 'center' : undefined }}>
            {t(K.ProbSubline)}
          </p>

          <div style={{
            marginTop: 8,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 16,
          }}>
            {/* Old way */}
            <div className="lp-card" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#e8e4df' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'rgba(16,54,66,0.5)',
                margin: '0 0 20px 0' }}>
                {t(K.OldWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.OldWay1, K.OldWay2, K.OldWay3, K.OldWay4, K.OldWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                      fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✕</span>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>

            {/* riise way */}
            <div className="lp-card lp-card-riise" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(17,109,110,0.1)' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: '#103642', margin: '0 0 20px 0' }}>
                {t(K.NewWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.NewWay1, K.NewWay2, K.NewWay3, K.NewWay4, K.NewWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      <Icon name="tealCheckCircle" size={18} />
                    </div>
                    <p style={{ ...SANS, fontSize: 15, color: '#103642',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Results ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={20}>
          <div style={{
            display: 'flex',
            flexDirection: isDesktop ? 'row' : 'column-reverse',
            alignItems: 'center',
            gap: isDesktop ? 48 : 24,
          }}>
            <div style={{ width: isDesktop ? '50%' : '100%' }}>
              <img src={ResultsCustomerPhoto} alt="" className="lp-results-photo"
                style={{ width: '100%', objectFit: 'cover', borderRadius: 16, display: 'block' }} />
            </div>

            <div style={{ width: isDesktop ? '50%' : '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ textAlign: isDesktop ? 'left' : 'center' }}>
                <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: '#116D6E', margin: 0 }}>
                  {t(K.ResultsSectionLabel)}
                </p>
                <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
                  color: '#103642', margin: 0, marginTop: 4 }}>
                  {t(K.ResultsH1)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ResultsH2)}</em>
                </h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { val: K.ResultsStat1Val, desc: K.ResultsStat1Desc, Icon: TrendUpIcon },
                  { val: K.ResultsStat2Val, desc: K.ResultsStat2Desc, Icon: ThumbsUpIcon },
                ].map(({ val, desc, Icon }) => (
                  <div key={val} style={{
                    display: 'flex', flexDirection: 'column', gap: 8,
                    background: 'rgba(16,54,66,0.04)', border: '1px solid rgba(16,54,66,0.1)',
                    borderRadius: 12, padding: 16,
                  }}>
                    <Icon style={{ width: 28, height: 28, color: '#116D6E' }} />
                    <p style={{ ...SERIF, fontSize: isDesktop ? 38 : 32, lineHeight: 1, fontWeight: 400,
                      color: '#103642', margin: 0 }}>
                      {t(val)}
                    </p>
                    <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.7)',
                      margin: 0, lineHeight: 1.4 }}>
                      {t(desc)}
                    </p>
                  </div>
                ))}
              </div>

              <p style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.3)',
                textAlign: isDesktop ? 'left' : 'center', margin: 0, marginTop: -8 }}>
                {t(K.ResultsSource)}
              </p>

              <div style={{ alignSelf: isDesktop ? 'flex-start' : 'center', width: isDesktop ? 'auto' : '100%' }}>
                <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
              </div>
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Solution reveal ── */}
      <section style={{ background: '#f5f7f7', paddingTop: 64, paddingBottom: 32 }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `0 ${px}px` }}>
          {isDesktop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 48, marginBottom: 48 }}>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 140, height: 'auto', flexShrink: 0 }} />
              <div>
                <h2 style={{ ...SERIF, fontSize: 36, lineHeight: 1.15, fontWeight: 400,
                  color: '#103642', margin: '0 0 12px 0' }}>
                  {t(K.IngrHeading)}
                </h2>
                <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.5)',
                  margin: 0, lineHeight: 1.6 }}>
                  {t(K.IngrSubline)}
                </p>
              </div>
            </div>
          ) : (
            <Stack gap={24} align="center" style={{ textAlign: 'center', marginBottom: 36 }}>
              <h2 style={{ ...SERIF, fontSize: 28, lineHeight: 1.15, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.IngrHeading)}
              </h2>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 150, height: 'auto' }} />
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.5)',
                margin: 0, lineHeight: 1.6 }}>
                {t(K.IngrSubline)}
              </p>
            </Stack>
          )}
        </div>
        <IngredientsSlider />
      </section>

      {/* ── How it works ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={isDesktop ? 48 : 40}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.HowWorksHeading)}
          </h2>

          {isDesktop ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 32 }}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} className="lp-card" style={{
                  background: '#f5f7f7', borderRadius: 16, padding: 28,
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  <StepNumber n={n} />
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 600,
                    color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                    margin: 0, lineHeight: 1.65 }}>
                    {t(desc)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <Stack gap={32}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <StepNumber n={n} />
                  <Stack gap={8}>
                    <p style={{ ...SANS, fontSize: 16, fontWeight: 600,
                      color: '#103642', margin: 0 }}>
                      {t(title)}
                    </p>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.65 }}>
                      {t(desc)}
                    </p>
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>

      {/* ── Mid-page CTA ── */}
      <Section bg="#103642" maxW={maxW} px={px}>
        <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(K.FinalHeading)}
          </h2>
          <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.65)',
            margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
            {t(K.FinalSubline)}
          </p>
          <div style={{ width: isDesktop ? 'auto' : '100%' }}>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
          </div>
        </Stack>
      </Section>

      {/* ── Expert ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 450 }}>
              <PractitionerBadge />
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Featured testimonial (Jan) ── */}
      <Section bg="#f5f7f7" py={56} maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={20}>
          <StarRow count={5} />
          <p style={{ ...SANS, fontSize: isDesktop ? 22 : 18, lineHeight: 1.6, fontWeight: 400,
            color: '#103642', margin: 0, fontStyle: 'italic' }}>
            {t(K.FeatQuote)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={JanPhoto} alt="Jan"
              style={{ width: 48, height: 48, borderRadius: '50%',
                objectFit: 'cover', flexShrink: 0 }} />
            <p style={{ ...SANS, fontSize: 14, fontWeight: 600,
              color: 'rgba(16,54,66,0.6)', margin: 0 }}>
              {t(K.FeatAuthor)}
            </p>
          </div>
        </Stack>
      </Section>

      {/* ── Video testimonials ── */}
      <BoomTestimonials />

      {/* ── Comparison table ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <MakeChoice />
      </Section>

      {/* ── FAQ ── */}
      <Section bg="#fff" maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.FaqHeading)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column',
            borderTop: '1px solid #f0f0f0' }}>
            {[
              { q: K.Faq1Q, a: K.Faq1A },
              { q: K.Faq2Q, a: K.Faq2A },
              { q: K.Faq3Q, a: K.Faq3A },
              { q: K.Faq4Q, a: K.Faq4A },
              { q: K.Faq5Q, a: K.Faq5A },
            ].map(({ q, a }) => (
              <div key={q} className="lp-faq-row" style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 0' }}>
                <Accordion heading={t(q)}>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.7)',
                    lineHeight: 1.65, margin: '12px 0 16px 0' }}>
                    {t(a)}
                  </p>
                </Accordion>
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `80px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 40 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 'auto' : '100%' }}>
              <CtaButton full={!isDesktop}>{t(K.FinalCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)',
                textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      {/* ── Reviews carousel ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ReviewsHeading)}
          </h2>
          <ReviewsMobile />
        </Stack>
      </Section>

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise"
              style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                {t(K.PrivacyPolicy)}
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default PillsOrSachet;
